import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardDialog } from "@/components/CardDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import { CategoriesDialog } from "@/components/CategoriesDialog";
import { AdminsDialog } from "@/components/AdminsDialog";
import { AffiliateCard } from "@/components/AffiliateCard";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Instagram, Youtube, Facebook, Music2,
  Settings, Plus, Pencil, LogIn, LogOut, ShieldCheck, ChevronDown, Tag, Users,
} from "lucide-react";
import { toast } from "sonner";

const socialMeta = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "tiktok", label: "TikTok", Icon: Music2 },
  { key: "facebook", label: "Facebook", Icon: Facebook },
];

export default function Home() {
  const { isAdmin, isHeadAdmin, mustChangePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [cardDialog, setCardDialog] = useState({ open: false, card: null });
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (mustChangePassword) navigate("/set-password");
  }, [mustChangePassword, navigate]);

  const loadCards = useCallback(async () => {
    const { data } = await api.get("/cards");
    setCards(data);
  }, []);
  const loadCategories = useCallback(async () => {
    const { data } = await api.get("/categories");
    setCategories(data);
  }, []);

  const loadAll = useCallback(async () => {
    setLoadError(false);
    try {
      const { data } = await api.get("/config");
      setConfig(data);
      loadCards().catch(() => {});
      loadCategories().catch(() => {});
    } catch (e) {
      setLoadError(true);
    }
  }, [loadCards, loadCategories]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const deleteCard = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/cards/${id}`);
    toast.success("Item deleted");
    loadCards();
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4 text-zinc-500 font-mono text-sm px-4 text-center">
        {loadError ? (
          <>
            <p data-testid="load-error">Couldn't reach the server. Please check your connection.</p>
            <button data-testid="retry-btn" onClick={loadAll}
              className="bg-white text-black font-bold px-6 py-3 uppercase tracking-wider text-xs hover:bg-[#FF3B30] hover:text-white transition-colors">
              Retry
            </button>
          </>
        ) : (
          <span data-testid="loading-indicator" className="animate-pulse">Loading…</span>
        )}
      </div>
    );
  }

  const activeSocials = socialMeta.filter((s) => config.social_links?.[s.key]);
  const visibleCards = activeCat === "all" ? cards : cards.filter((c) => c.category_id === activeCat);

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-white grain">
      {config.background_image_url && (
        <div className="fixed inset-0 z-0">
          <img src={imgUrl(config.background_image_url)} alt="" className="w-full h-full object-cover opacity-[0.12]" />
          <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        </div>
      )}

      {/* Top bar */}
      <header className="relative z-20 sticky top-0 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">Troy<span className="text-[#FF3B30]">/</span>Tested</span>
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] border border-[#FF3B30]/40 px-2 py-1">
                <ShieldCheck size={12} /> {isHeadAdmin ? "Head Admin" : "Edit Mode"}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="admin-menu-btn"
                    className="flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors text-xs font-mono uppercase tracking-wider">
                    Manage <ChevronDown size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#121212] border border-white/15 rounded-none text-white font-mono text-xs">
                  <DropdownMenuItem data-testid="menu-settings" onClick={() => setSettingsOpen(true)} className="rounded-none cursor-pointer focus:bg-[#FF3B30] focus:text-white">
                    <Settings size={14} className="mr-2" /> Page Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-categories" onClick={() => setCatsOpen(true)} className="rounded-none cursor-pointer focus:bg-[#FF3B30] focus:text-white">
                    <Tag size={14} className="mr-2" /> Categories
                  </DropdownMenuItem>
                  {isHeadAdmin && (
                    <DropdownMenuItem data-testid="menu-admins" onClick={() => setAdminsOpen(true)} className="rounded-none cursor-pointer focus:bg-[#FF3B30] focus:text-white">
                      <Users size={14} className="mr-2" /> Manage Admins
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="rounded-none cursor-pointer focus:bg-white/10">
                    <LogOut size={14} className="mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <button data-testid="login-nav-btn" onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors text-xs font-mono uppercase tracking-wider">
              <LogIn size={14} /> Sign In
            </button>
          )}
        </div>
      </header>

      {/* Banner */}
      <section className="relative z-10 group">
        <div className="relative w-full h-[52vh] min-h-[380px] flex items-center justify-center border-b border-white/10 overflow-hidden">
          {config.banner_image_url && (
            <img src={imgUrl(config.banner_image_url)} alt="banner" className="absolute inset-0 w-full h-full object-cover" data-testid="banner-image" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/60 to-black/40" />
          <div className="relative z-10 text-center px-4 fade-up">
            <h1 data-testid="banner-title" className="font-heading text-white text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter drop-shadow-2xl">
              {config.banner_title}
            </h1>
            {config.banner_subtitle && (
              <p className="mt-4 font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-zinc-300">{config.banner_subtitle}</p>
            )}
          </div>
          {isAdmin && (
            <button data-testid="edit-banner-btn" onClick={() => setSettingsOpen(true)}
              className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-black/80 backdrop-blur border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors">
              <Pencil size={13} /> Edit Banner
            </button>
          )}
        </div>
      </section>

      {/* Social links */}
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap justify-center gap-3 sm:gap-4">
          {activeSocials.length === 0 && (
            <p className="text-zinc-600 font-mono text-xs uppercase tracking-widest">No social links yet</p>
          )}
          {activeSocials.map(({ key, label, Icon }) => (
            <a key={key} href={config.social_links[key]} target="_blank" rel="noopener noreferrer"
              data-testid={`social-link-${key}`}
              className="flex items-center gap-2 px-5 py-3 bg-[#121212] border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors text-xs font-mono uppercase tracking-wider">
              <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Category tabs + grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          {categories.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="category-tabs">
              <button data-testid="cat-tab-all" onClick={() => setActiveCat("all")}
                className={`px-4 py-2 border text-xs font-mono uppercase tracking-wider transition-colors ${activeCat === "all" ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "bg-[#121212] border-white/10 hover:border-white/30"}`}>
                All
              </button>
              {categories.map((c) => (
                <button key={c.id} data-testid={`cat-tab-${c.id}`} onClick={() => setActiveCat(c.id)}
                  className={`px-4 py-2 border text-xs font-mono uppercase tracking-wider transition-colors ${activeCat === c.id ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "bg-[#121212] border-white/10 hover:border-white/30"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          ) : <div />}
          {isAdmin && (
            <button data-testid="add-card-btn" onClick={() => setCardDialog({ open: true, card: null })}
              className="flex items-center gap-2 bg-white text-black font-bold px-5 py-3 uppercase tracking-wider text-xs hover:bg-[#FF3B30] hover:text-white transition-colors">
              <Plus size={16} /> Add Item
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleCards.map((card, i) => (
            <AffiliateCard key={card.id} card={card} index={i} isAdmin={isAdmin}
              onEdit={() => setCardDialog({ open: true, card })}
              onDelete={() => deleteCard(card.id)} />
          ))}
        </div>

        {visibleCards.length === 0 && (
          <div className="border border-dashed border-white/15 py-24 text-center">
            <p className="text-zinc-500 font-mono text-sm uppercase tracking-widest">No items here yet</p>
          </div>
        )}
      </section>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">© Troy Tested</p>
      </footer>

      {isAdmin && (
        <>
          <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} config={config} onSaved={setConfig} />
          <CategoriesDialog open={catsOpen} onOpenChange={setCatsOpen} onChanged={loadCategories} />
          {isHeadAdmin && <AdminsDialog open={adminsOpen} onOpenChange={setAdminsOpen} />}
          <CardDialog open={cardDialog.open} onOpenChange={(o) => setCardDialog((s) => ({ ...s, open: o }))}
            card={cardDialog.card} categories={categories} onSaved={loadCards} />
        </>
      )}
    </div>
  );
}
